/**
 * Dynamic Theme Generator — Runtime Color Token Engine
 *
 * Generates design system tokens from a single source (brand) color using
 * OKLCH tonal palettes (shared math in radiant-react's oklch-palette).
 *
 * Architecture:
 *   Source Color → Tonal Palettes (brand + neutral) → System + Component Tokens
 *
 * This module is purely functional — no DOM access, no side-effects.
 * Pair with apply-theme.ts to inject tokens into the page.
 *
 * Only brand-dependent tokens are emitted. Fixed-semantic tokens
 * (success/warning/failure/information, accent-*, attribute/measure/date/error
 * chips, shadows) are NOT emitted — the static SCSS light/dark declarations
 * in radiant-styles apply unchanged, so their values can never drift from the
 * design system.
 *
 * Calibration invariant (pinned by theme-generator.spec.ts): generating from
 * the default brand blue #2770ef must reproduce the static radiant theme —
 * the tone constants below are the measured Lr-tones of the static ramps in
 * radiant-styles/css/foundations/_tokens-reference.scss.
 */

import {
    contrastRatio,
    deriveStateColors,
    hexToOklch,
    makeTonalPalette,
    oklabLToTone,
    oklchToHexGamutMapped,
    OklchTonalPalette,
    pickOnColor,
} from '@thoughtspot/radiant-react/widgets/color/oklch-palette';

// =========================================================================
// Types
// =========================================================================

/** Token map — keys are token names, values are hex or rgba() strings */
export interface TokenMap {
    [tokenName: string]: string;
}

/** Full output of the generator */
export interface GeneratedTheme {
    system: { light: TokenMap; dark: TokenMap };
    component: { light: TokenMap; dark: TokenMap };
}

// =========================================================================
// Tone constants — measured Lr-tones of the static ramps
// =========================================================================

/**
 * Neutral palettes keep the brand hue but cap chroma at an absolute value so
 * vivid/warm brands cannot visibly tint surfaces (no brown dark dialogs for
 * a yellow brand). The static dark-gray ramp measures C ≈ 0.003–0.013, so
 * 0.012 reproduces its subtle tint for the default blue.
 */
const NEUTRAL_CHROMA = 0.012;

/** Light-theme gray ramp ($rd-ref-gray-*): scale step → Lr tone. */
const GRAY = {
    g00: 100, // #ffffff
    g10: 98, //  #f6f8fa
    g20: 94, //  #eaedf2
    g30: 89, //  #dbdfe7
    g40: 80, //  #c0c6cf
    g50: 70, //  #a5acb9
    g60: 53, //  #777e8b
    g70: 35, //  #4a515e
    g80: 25, //  #323946
    g90: 15, //  #1d232f
};

/** Dark-theme gray ramp ($rd-ref-dark-gray-*): scale step → Lr tone. */
const DARK_GRAY = {
    d00: 11, // #18191B
    d05: 14, // #1F2024
    d10: 17, // #26282C
    d15: 21, // #2F3137
    d20: 23, // #34363C
    d30: 32, // #464A4E
    d40: 43, // #60656c
    d50: 55, // #808389
    d60: 65, // #9C9EA5
    d70: 72, // #afb2b6
    d80: 81, // #c7c9cc
    d90: 89, // #dfe0e2
};

/** Brand ramp ($rd-ref-brand-* = blue by default): scale step → Lr tone. */
const BRAND = {
    b20: 92, // #dee8fa
    b30: 87, // #cedcf5
    b40: 80, // #abc7f9
    b50: 66, // #71a1f4
    b60: 51, // #2770ef
    b70: 40, // #2359b6
    b80: 25, // #163772
};

const WCAG_AA_TEXT = 4.5;

// =========================================================================
// Helpers
// =========================================================================

interface Palettes {
    brand: OklchTonalPalette;
    gray: OklchTonalPalette;
}

/** "#rrggbb" + alpha → "rgba(r, g, b, a)" (the form the SCSS alphas compile to). */
function rgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Lr tone of a hex color. */
function toneOf(hex: string): number {
    return oklabLToTone(hexToOklch(hex)[0]);
}

/**
 * Walk the palette from startTone in `step` direction (±2 tones at a time)
 * until the color reaches the required contrast against bg, or the tone
 * range is exhausted.
 */
function toneWithMinContrast(
    palette: OklchTonalPalette,
    startTone: number,
    bgHex: string,
    step: number,
): string {
    let tone = Math.min(100, Math.max(0, startTone));
    let hex = palette.tone(tone);
    while (
        contrastRatio(hex, bgHex) < WCAG_AA_TEXT &&
        tone + step >= 0 &&
        tone + step <= 100
    ) {
        tone += step;
        hex = palette.tone(tone);
    }
    return hex;
}

/**
 * Brand text color (links, tertiary buttons, selected nav items):
 * stay as close to the brand as possible while readable on the mode's
 * base surface.
 *   Light: start at the source tone (capped at the static brand-60 tone so a
 *          light brand doesn't produce illegible light text), darken to 4.5:1.
 *   Dark:  start at the source tone (raised to at least the static brand-50
 *          tone), lighten to 4.5:1 against the dark base surface.
 */
function contentBrand(palettes: Palettes, isDark: boolean): string {
    const { brand, gray } = palettes;
    const sourceTone = toneOf(brand.sourceHex);
    if (isDark) {
        return toneWithMinContrast(
            brand,
            Math.max(sourceTone, BRAND.b50),
            gray.tone(DARK_GRAY.d05),
            2,
        );
    }
    return toneWithMinContrast(
        brand,
        Math.min(sourceTone, BRAND.b60),
        gray.tone(GRAY.g00),
        -2,
    );
}

// =========================================================================
// Primary CTA cluster
// =========================================================================
//
// Background, hover/pressed, text and disabled state are derived together so
// they can never disagree:
//   - hover/pressed are RELATIVE tone steps from the actual rendered
//     background (an absolute tone would turn a light-yellow brand's hover
//     dark brown),
//   - the text ("on color") is contrast-picked against that background, and
//     its hover/pressed tints follow the picked polarity — mirroring the
//     static theme, where light mode uses white → pale-brand text and dark
//     mode uses dark-neutral text steps.

interface CtaColors {
    bg: string;
    hover: string;
    pressed: string;
    /** Contrast-picked text color on `bg` (≥ 4.5:1 wherever possible). */
    content: string;
    contentHover: string;
    contentPressed: string;
    /** Desaturated, surface-pulled variant so disabled CTAs read disabled. */
    disabled: string;
}

function deriveCta(palettes: Palettes, isDark: boolean): CtaColors {
    const { brand, gray } = palettes;
    // Light mode: the EXACT color the admin picked. Dark mode: the tonal
    // equivalent of the static dark theme's primary (brand-50).
    const bg = isDark ? brand.tone(BRAND.b50) : brand.sourceHex;

    // Static ramps darken on hover in light mode and lighten in dark mode.
    const { hover, pressed } = deriveStateColors(bg, {
        preferDarken: !isDark,
    });

    const lightCandidate = gray.tone(GRAY.g00);
    // Dark mode: the CTA is always brand tone b50, against which the static
    // dark text (dark-gray-05) always clears AA. Light mode: the CTA is the
    // arbitrary source color — mid-luminance brands (Y ≈ 0.17–0.21) have a
    // band where neither white nor a near-black-ish gray reaches 4.5:1, so
    // the dark candidate must be true black (tone 0) to guarantee AA
    // everywhere white fails.
    const darkCandidate = isDark ? gray.tone(DARK_GRAY.d05) : gray.tone(0);
    const content = pickOnColor(bg, {
        light: lightCandidate,
        dark: darkCandidate,
    });
    const lightText = content === lightCandidate;

    // Disabled: mostly keep the CTA's lightness but pull it toward the
    // surface and strip most of the chroma, so it reads inert even for
    // vivid brands (opacity alone does not).
    const [L, C, H] = hexToOklch(bg);
    const surfaceL = isDark ? 0.25 : 0.95;
    const disabled = oklchToHexGamutMapped(
        0.75 * L + 0.25 * surfaceL,
        Math.min(C * 0.3, 0.03),
        H,
    );

    return {
        bg,
        hover,
        pressed,
        content,
        contentHover: lightText
            ? brand.tone(BRAND.b20)
            : gray.tone(DARK_GRAY.d15),
        contentPressed: lightText
            ? brand.tone(BRAND.b30)
            : gray.tone(DARK_GRAY.d30),
        disabled,
    };
}

// =========================================================================
// System Token Generation
// =========================================================================

/**
 * Generate the 24 brand-dependent system tokens (Background/Content/Border)
 * for one mode. Values are hex or rgba() strings, ready for CSS injection.
 */
function generateSystemModeTokens(
    palettes: Palettes,
    isDark: boolean,
): TokenMap {
    const { brand, gray } = palettes;
    const contentBrandValue = contentBrand(palettes, isDark);

    return {
        // ================================================================
        // BACKGROUND (12 tokens)
        // ================================================================
        'background-sunken': isDark
            ? gray.tone(DARK_GRAY.d00)
            : gray.tone(GRAY.g10),
        'background-base': isDark
            ? gray.tone(DARK_GRAY.d05)
            : gray.tone(GRAY.g00),
        'background-on-base': isDark
            ? gray.tone(DARK_GRAY.d10)
            : gray.tone(GRAY.g10),
        'background-raised': isDark
            ? gray.tone(DARK_GRAY.d15)
            : gray.tone(GRAY.g00),
        'background-active': isDark
            ? gray.tone(DARK_GRAY.d40)
            : gray.tone(GRAY.g00),
        'background-secondary-action': isDark
            ? gray.tone(DARK_GRAY.d20)
            : gray.tone(GRAY.g20),
        'background-secondary-hover': isDark
            ? gray.tone(DARK_GRAY.d30)
            : gray.tone(GRAY.g30),
        // Light mode: exact source color for brand fills; dark mode: the
        // lighter tonal variant the static dark theme uses (brand-50).
        'background-brand': isDark ? brand.tone(BRAND.b50) : brand.sourceHex,
        'background-overlay': isDark
            ? rgba(gray.tone(3), 0.73)
            : rgba(gray.tone(16), 0.8),
        'background-inverse': isDark
            ? gray.tone(DARK_GRAY.d90)
            : gray.tone(GRAY.g90),
        // Translucent by design (12% veils) so they compose over any
        // surface — the static tokens are alpha colors, not solids.
        'background-ghost-hover': rgba(gray.tone(GRAY.g40), 0.12),
        'background-ghost-highlight': rgba(brand.tone(BRAND.b50), 0.12),

        // ================================================================
        // CONTENT (5 tokens)
        // ================================================================
        'content-primary': isDark
            ? gray.tone(DARK_GRAY.d90)
            : gray.tone(GRAY.g90),
        'content-secondary': isDark
            ? gray.tone(DARK_GRAY.d60)
            : gray.tone(GRAY.g60),
        'content-tertiary': isDark
            ? gray.tone(DARK_GRAY.d40)
            : gray.tone(GRAY.g40),
        'content-brand': contentBrandValue,
        // Paired with background-inverse (tooltips etc.) and the fallback
        // text on brand fills. Text on the primary CTA specifically is the
        // contrast-picked button-primary-content, bridged by apply-theme.
        'content-inverse': isDark
            ? gray.tone(DARK_GRAY.d05)
            : gray.tone(GRAY.g00),

        // ================================================================
        // BORDER (7 tokens)
        // ================================================================
        'border-bold': isDark ? gray.tone(DARK_GRAY.d40) : gray.tone(GRAY.g40),
        'border-bold-hover': isDark
            ? gray.tone(DARK_GRAY.d50)
            : gray.tone(GRAY.g50),
        'border-subtle': isDark
            ? gray.tone(DARK_GRAY.d20)
            : gray.tone(GRAY.g20),
        'border-subtle-hover': isDark
            ? gray.tone(DARK_GRAY.d30)
            : gray.tone(GRAY.g30),
        'border-brand': isDark ? brand.tone(BRAND.b50) : contentBrandValue,
        // The static maps intentionally invert these across modes:
        // light uses brand-50, dark uses brand-60.
        'border-focus': isDark ? brand.tone(BRAND.b60) : brand.tone(BRAND.b50),
        'border-gap': isDark ? gray.tone(DARK_GRAY.d05) : gray.tone(GRAY.g00),
    };
}

// =========================================================================
// Component Token Generation
// =========================================================================

/**
 * Generate the brand-dependent component tokens (Button, neutral Chips,
 * Toggle) for one mode, plus two generator-only tokens with no SCSS
 * counterpart: button-primary-content (contrast-picked CTA text, bridged to
 * --ts-var-button--primary-color) and button-primary-disabled (consumed by
 * the disabled rule in radiant-react button.module.scss).
 */
function generateComponentModeTokens(
    palettes: Palettes,
    isDark: boolean,
): TokenMap {
    const { brand, gray } = palettes;
    const cta = deriveCta(palettes, isDark);

    return {
        // ================================================================
        // BUTTON
        // ================================================================
        'button-primary-default': cta.bg,
        'button-primary-hover': cta.hover,
        'button-primary-pressed': cta.pressed,
        'button-primary-on-color-default': cta.content,
        'button-primary-on-color-hover': cta.contentHover,
        'button-primary-on-color-pressed': cta.contentPressed,
        'button-secondary-default': isDark
            ? gray.tone(DARK_GRAY.d20)
            : gray.tone(GRAY.g20),
        'button-secondary-hover': isDark
            ? gray.tone(DARK_GRAY.d30)
            : gray.tone(GRAY.g30),
        'button-secondary-pressed': isDark
            ? gray.tone(DARK_GRAY.d40)
            : gray.tone(GRAY.g40),
        'button-tertiary-hover': rgba(gray.tone(GRAY.g40), 0.12),
        'button-tertiary-pressed': rgba(brand.tone(BRAND.b50), 0.12),
        'button-bg-overlay': isDark
            ? rgba(gray.tone(GRAY.g20), 0.6)
            : rgba(gray.tone(18), 0.6),

        // Generator-only tokens (no SCSS map entry — see doc comment above)
        'button-primary-content': cta.content,
        'button-primary-disabled': cta.disabled,

        // ================================================================
        // CHIP – Loading
        // ================================================================
        'chip-loading-default': isDark
            ? gray.tone(DARK_GRAY.d10)
            : gray.tone(GRAY.g10),

        // ================================================================
        // CHIP – Filter (neutral)
        // ================================================================
        'chip-filter-default': isDark
            ? gray.tone(DARK_GRAY.d15)
            : gray.tone(GRAY.g20),
        'chip-filter-hover': isDark
            ? gray.tone(DARK_GRAY.d20)
            : gray.tone(GRAY.g30),
        'chip-filter-pressed': isDark
            ? gray.tone(DARK_GRAY.d30)
            : gray.tone(GRAY.g40),
        'chip-filter-icon': isDark
            ? gray.tone(DARK_GRAY.d50)
            : gray.tone(GRAY.g60),
        'chip-filter-active-border': isDark
            ? gray.tone(DARK_GRAY.d20)
            : gray.tone(GRAY.g30),

        // ================================================================
        // CHIP – Input (neutral, with selected states)
        // ================================================================
        'chip-input-default': isDark
            ? gray.tone(DARK_GRAY.d00)
            : gray.tone(GRAY.g00),
        'chip-input-hover': isDark
            ? gray.tone(DARK_GRAY.d10)
            : gray.tone(GRAY.g10),
        'chip-input-pressed': isDark
            ? gray.tone(DARK_GRAY.d15)
            : gray.tone(GRAY.g20),
        'chip-input-selected-default': isDark
            ? gray.tone(DARK_GRAY.d70)
            : gray.tone(GRAY.g70),
        'chip-input-selected-hover': isDark
            ? gray.tone(DARK_GRAY.d80)
            : gray.tone(GRAY.g80),
        'chip-input-selected-pressed': isDark
            ? gray.tone(DARK_GRAY.d90)
            : gray.tone(GRAY.g90),

        // ================================================================
        // TOGGLE
        // ================================================================
        'toggle-track-off': isDark
            ? gray.tone(DARK_GRAY.d30)
            : gray.tone(GRAY.g40),
        'toggle-track-on': isDark
            ? brand.tone(BRAND.b80)
            : brand.tone(BRAND.b30),
        'toggle-thumb-off': isDark
            ? gray.tone(DARK_GRAY.d70)
            : gray.tone(GRAY.g00),
        'toggle-thumb-on': isDark ? brand.tone(BRAND.b50) : brand.sourceHex,
        'toggle-thumb-outline-off': isDark
            ? gray.tone(DARK_GRAY.d70)
            : gray.tone(GRAY.g30),
    };
}

// =========================================================================
// Public API
// =========================================================================

/**
 * Generate all brand-dependent design system tokens from a single source
 * (brand) color.
 *
 * Returns system and component token maps for both light and dark modes.
 * Values are hex strings, or rgba() strings for the translucent-by-design
 * tokens (ghost hover/highlight, overlays, tertiary button states).
 *
 * @param primaryHex - Source/brand color in hex (e.g., "#2770ef")
 *
 * @example
 * ```ts
 * const theme = generateTheme('#2770ef');
 * console.log(theme.system.light['background-brand']); // "#2770ef"
 * console.log(theme.system.dark['background-brand']);  // lighter variant
 * ```
 */
export function generateTheme(primaryHex: string): GeneratedTheme {
    const sourceHex = primaryHex.toLowerCase();
    const palettes: Palettes = {
        brand: makeTonalPalette(sourceHex),
        gray: makeTonalPalette(sourceHex, { maxChroma: NEUTRAL_CHROMA }),
    };

    return {
        system: {
            light: generateSystemModeTokens(palettes, false),
            dark: generateSystemModeTokens(palettes, true),
        },
        component: {
            light: generateComponentModeTokens(palettes, false),
            dark: generateComponentModeTokens(palettes, true),
        },
    };
}
